import { getMobileBrowserOS, getUiBrowser, isUiTablet } from './browser';
import {
    LIVE_ASK_CHANGED,
    LIVE_AUCTION_ENDED,
    LIVE_AUCTION_MOVED,
    LIVE_AUCTION_OPENED,
    LIVE_AUCTION_PAUSED,
    LIVE_AUCTION_RESUMED,
    LIVE_AUCTION_STARTED,
    LIVE_BID_ACCEPTED,
    LIVE_BID_RETRACTED,
    LIVE_LOT_CLOSED,
    LIVE_LOT_PASSED,
    LIVE_LOT_REOPENED, // LIVE_LOT_SKIPPED,
    LIVE_LOT_SOLD,
    LIVE_LOT_UNSOLD,
    LIVE_MISSIVE,
    LIVE_NETWORK_DISCONNECTED,
    LIVE_NETWORK_RECONNECTED,
    LIVE_NEXT_LOT_LOADED,
    LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB,
    LIVE_UPDATE_ACTIVE_BIDDERS_SFS,
    LIVE_UPDATE_TIMESTAMP,
    LOAD_CATALOGS_SUCCESS,
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
} from './actions';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { GlobalState } from '../rootReducer';
import { LiveCatalogData } from '@/types/LiveCatalogData';
import { LiveItemData } from '@/types/LiveItemData';

/* helpers */
const getCurrentTimestamp = () => new Date().getTime();

interface DefaultState {
    readonly catalogs: {
        [catalogId: number]: LiveCatalogData;
    };
    readonly items: {
        [itemId: number]: LiveItemData;
    };
    readonly timestamps: {
        [catalogId: number]: number;
    };
}

/* reducer */
const DEFAULT_STATE: DefaultState = {
    catalogs: {},
    items: {},
    timestamps: {},
};

export type State = typeof DEFAULT_STATE;

const DEFAULT_CATALOG = {
    activeBidder: false,
    approved: false,
    bidderOutbid: false,
    bidders: 0, // this is computed in the selector
    closed: true,
    currentItem: 0,
    currentItemIndex: 0,
    networkStatus: 'connected',
    pubnubBidderCount: 0,
    pubNubPublishKey: '',
    sfsBidderCount: 0,
    status: 'notLoaded',
    won: false,
};

const setCatalogLive = (state: State, catalogId: number) => {
    const catalogs = cloneDeep(state.catalogs);
    catalogs[catalogId] = catalogs[catalogId] || cloneDeep(DEFAULT_CATALOG);
    catalogs[catalogId].status = 'live';
    return catalogs;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let catalogs;
    let items;

    switch (action.type) {
        case LIVE_UPDATE_TIMESTAMP:
            return {
                ...state,
                timestamps: {
                    ...state.timestamps,
                    [action.payload.catalogId]: action.payload.timestamp,
                },
            };
        case LIVE_ASK_CHANGED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
            items = cloneDeep(state.items);
            items[action.payload.itemId] = items[action.payload.itemId] || {};
            items[action.payload.itemId].currentAskPrice = action.payload.ask;
            return {
                ...state,
                catalogs,
                items,
            };
        case LIVE_AUCTION_ENDED:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].status = 'done';
            return {
                ...state,
                catalogs,
            };
        case LIVE_AUCTION_MOVED:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].status = 'notLoaded';
            return {
                ...state,
                catalogs,
            };
        case LIVE_AUCTION_OPENED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            return {
                ...state,
                catalogs,
            };
        case LIVE_AUCTION_PAUSED:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].status = 'paused';
            return {
                ...state,
                catalogs,
            };
        case LIVE_AUCTION_RESUMED:
        case LIVE_AUCTION_STARTED:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].status = 'live';
            return {
                ...state,
                catalogs,
            };
        case LIVE_BID_ACCEPTED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].activeBidder =
                Boolean(catalogs[action.payload.catalogId].activeBidder) || Boolean(action.payload.myBid);
            catalogs[action.payload.catalogId].bidderOutbid = Boolean(action.payload.bidderOutbid);
            catalogs[action.payload.catalogId].closed = false;
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
            items = cloneDeep(state.items);
            items[action.payload.itemId] = items[action.payload.itemId] || { created: getCurrentTimestamp() };

            if (items[action.payload.itemId].created < action.payload.created) {
                items[action.payload.itemId].created = action.payload.created;
                items[action.payload.itemId].currentAskPrice = action.payload.ask;
                items[action.payload.itemId].leadingBid = action.payload.amount;
                items[action.payload.itemId].leadingBidder = action.payload.bidderId;
                items[action.payload.itemId].bidCount = items[action.payload.itemId].bidCount
                    ? items[action.payload.itemId].bidCount + 1
                    : 1;
            }

            return {
                ...state,
                catalogs,
                items,
            };

        case LIVE_BID_RETRACTED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = false;
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
            items = cloneDeep(state.items);
            items[action.payload.itemId] = items[action.payload.itemId] || {};
            items[action.payload.itemId].created = action.payload.created;
            items[action.payload.itemId].currentAskPrice = action.payload.ask;
            items[action.payload.itemId].leadingBid = action.payload.amount;
            items[action.payload.itemId].leadingBidder = action.payload.bidderId;
            items[action.payload.itemId].bidCount =
                items[action.payload.itemId].bidCount > 0 ? items[action.payload.itemId].bidCount - 1 : 0;
            return {
                ...state,
                catalogs,
                items,
            };
        case LIVE_LOT_CLOSED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;

            if (catalogs[action.payload.catalogId].currentItem === action.payload.itemId) {
                catalogs[action.payload.catalogId].closed = true;
            }

            return {
                ...state,
                catalogs,
            };
        case LIVE_LOT_PASSED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = true;
            return {
                ...state,
                catalogs,
            };
        case LIVE_LOT_REOPENED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].closed = false; // if from new system set variables, else don't -- old message does not have askprice

            if (
                action.payload.askPrice !== null &&
                action.payload.askPrice !== undefined &&
                action.payload.askPrice !== 0
            ) {
                catalogs[action.payload.catalogId].activeBidder =
                    Boolean(catalogs[action.payload.catalogId].activeBidder) || Boolean(action.payload.myBid);
                catalogs[action.payload.catalogId].bidderOutbid = false;
                catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
                catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
                catalogs[action.payload.catalogId].won = false;

                items = cloneDeep(state.items);

                items[action.payload.itemId] = items[action.payload.itemId] || {};
                items[action.payload.itemId].created = action.payload.created;
                items[action.payload.itemId].currentAskPrice = action.payload.askPrice;
                items[action.payload.itemId].bidCount = 0;
                items[action.payload.itemId].leadingBid = action.payload.leadingBid;
                items[action.payload.itemId].leadingBidder = action.payload.leadingBidder;

                return {
                    ...state,
                    catalogs,
                    items,
                };
            } else {
                return {
                    ...state,
                    catalogs,
                };
            }

        case LIVE_LOT_SOLD:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = true;
            catalogs[action.payload.catalogId].won = action.payload.myBid;
            return {
                ...state,
                catalogs,
            };
        case LIVE_LOT_UNSOLD:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = true;
            return {
                ...state,
                catalogs,
            };
        case LIVE_MISSIVE:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            return {
                ...state,
                catalogs,
            };
        case LIVE_NETWORK_DISCONNECTED:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].networkStatus = 'disconnected';
            return {
                ...state,
                catalogs,
            };
        case LIVE_NETWORK_RECONNECTED:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].networkStatus = 'connected';
            return {
                ...state,
                catalogs,
            };
        case LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};

            if (
                catalogs[action.payload.catalogId].pubNubPublishKey !== action.payload.publishKey ||
                catalogs[action.payload.catalogId].approved !== action.payload.approved
            ) {
                catalogs[action.meta.catalogId].approved = action.payload.approved;
                catalogs[action.meta.catalogId].pubNubPublishKey = action.payload.publishKey;
                return {
                    ...state,
                    catalogs,
                };
            }

            return state;
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};

            if (catalogs[action.payload.catalogId].status !== 'done') {
                catalogs[action.payload.catalogId].status = action.payload.status;
            }

            items = cloneDeep(state.items);

            if (action.payload.currentItem && action.payload.currentItem.itemId) {
                const { askPrice, itemId, itemIndex, leadingBid, leadingBidder } = action.payload.currentItem;

                catalogs[action.payload.catalogId].closed = false;
                catalogs[action.payload.catalogId].currentItem = itemId;
                catalogs[action.payload.catalogId].currentItemIndex = itemIndex || 0;
                catalogs[action.payload.catalogId].won = false;
                items[itemId] = items[itemId] || {};
                items[itemId].created = action.payload.created || getCurrentTimestamp();
                items[itemId].currentAskPrice = askPrice;
                items[itemId].leadingBid = leadingBid || 0;
                items[itemId].leadingBidder = leadingBidder || 0;
            }

            return {
                ...state,
                catalogs,
                items,
            };
        case LIVE_NEXT_LOT_LOADED:
            catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].activeBidder = false;
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = false;
            catalogs[action.payload.catalogId].currency = action.payload.currency;
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
            catalogs[action.payload.catalogId].won = false;
            items = cloneDeep(state.items);
            items[action.payload.itemId] = items[action.payload.itemId] || {};
            items[action.payload.itemId].created = action.payload.created;
            items[action.payload.itemId].currentAskPrice = action.payload.askPrice;
            items[action.payload.itemId].bidCount = 0;
            items[action.payload.itemId].leadingBid = 0;
            items[action.payload.itemId].leadingBidder = 0;
            return {
                ...state,
                catalogs,
                items,
            };
        case LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].pubnubBidderCount = action.payload.bidderCount;
            return {
                ...state,
                catalogs,
            };
        case LIVE_UPDATE_ACTIVE_BIDDERS_SFS:
            catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
            catalogs[action.payload.catalogId] = catalogs[action.payload.catalogId] || {};
            catalogs[action.payload.catalogId].sfsBidderCount = action.payload.bidderCount;
            return {
                ...state,
                catalogs,
            };
        case LOAD_CATALOGS_SUCCESS:
            catalogs = cloneDeep(state.catalogs);
            action.payload.catalogs.forEach((catalog) => {
                if (catalog.catalogStatus === 'done') {
                    // set the catalog to done if it is
                    catalogs[catalog.catalogId] = catalogs[catalog.catalogId] || {};
                    catalogs[catalog.catalogId].status = catalog.catalogStatus;
                }
            });
            return {
                ...state,
                catalogs,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.console;
const idSelector = (state: GlobalState, id) => id;

const catalogsSelector = createSelector(stateSelector, (state) => state.catalogs);
const itemsSelector = createSelector(stateSelector, (state) => state.items);
const timestampSelector = createSelector(stateSelector, (state) => state.timestamps);

export const getAllLiveCatalogs = createSelector(stateSelector, (state) => state.catalogs);

export const getLatestMessageTimestamp: (arg0: any, arg1: number) => number = createSelector(
    [timestampSelector, idSelector],
    (timestamps, id) => timestamps[id] || 0
);

export const getPubnubPublishKey: (arg0: any, arg1: number) => string = createSelector(
    [catalogsSelector, idSelector],
    (catalogs, id) => {
        const catalog = catalogs[id] || {};
        // @ts-ignore
        return catalog.pubNubPublishKey || '';
    }
);

export const getLiveCatalogData: (state: any, selector: any) => any = createSelector(
    [catalogsSelector, idSelector],
    (catalogs, id) => {
        const built = { ...DEFAULT_CATALOG, ...(catalogs[id] || {}) };
        built.bidders = built.sfsBidderCount + built.pubnubBidderCount || 1;
        return built;
    }
);

export const getLiveItemData: (state: any, selector: any) => any = createSelector(
    [itemsSelector, idSelector],
    (items, id) => items[id] || { bidCount: 0, currentAskPrice: 0, leadingBid: 0, leadingBidder: 0 }
);

export const getBrowserSessionCookieSuffix = (state: any) => {
    const mobileBrowser = getMobileBrowserOS(state);
    const tablet = isUiTablet(state);
    // @ts-ignore
    const { majorVersion, name } = getUiBrowser(state);
    const browser = `${name}${majorVersion}`;

    const deviceType = !mobileBrowser ? 'computer' : tablet ? 'tablet' : 'phone';
    return `::web-${deviceType}-${browser}`;
};
